// src/pages/RiskAnalysis.jsx
import React, { useEffect, useState } from "react";
import GaugeChart from "react-gauge-chart";
import { request } from "../../utils/request";
import "./dashboard.scss";

const CONTROL_WEIGHTS = {
  osVersion: 10,
  securityPatches: 10,
  firewall: 10,
  antivirus: 10,
  realTimeProtection: 10,
  diskEncrypted: 10,
  secureBootEnabled: 10,
  trustedCAs: 10,
  usbDevices: 10,
  networkPrivilege: 10,
};

/* Helpers to normalize various response shapes */
const normalizeArrayResp = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (resp.data) {
    if (Array.isArray(resp.data)) return resp.data;
    if (Array.isArray(resp.data.docs)) return resp.data.docs;
  }
  if (Array.isArray(resp.docs)) return resp.docs;
  const arr = Object.values(resp).find((v) => Array.isArray(v));
  return Array.isArray(arr) ? arr : [];
};

const parseSlashDate = (s) => {
  if (!s) return null;
  if (typeof s === "number") return new Date(s);
  if (typeof s === "string") {
    const m = s.match(/\/Date\((\-?\d+)\)\//);
    if (m && m[1]) return new Date(Number(m[1]));
    const n = Number(s);
    if (!Number.isNaN(n) && String(s).length > 6) {
      return new Date(n);
    }
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

/* Device validation heuristics (best-effort) */
const deviceChecks = {
  osVersion: (d) => {
    if (!d) return false;
    if (!d.osVersion || String(d.osVersion).trim() === "") return false;
    const patches = Array.isArray(d.securityPatches) ? d.securityPatches : [];
    const pending = Array.isArray(d.pendingUpdates) ? d.pendingUpdates : [];
    const now = Date.now();
    const maxAgeMs = 60 * 24 * 60 * 60 * 1000; // 60 days
    let latest = null;
    for (const p of patches) {
      const v = p?.InstalledOn?.value || p?.InstalledOn || p?.InstalledOn?.DateTime;
      const dt = parseSlashDate(v);
      if (dt && (!latest || dt.getTime() > latest.getTime())) latest = dt;
    }
    for (const p of pending) {
      const v = p?.InstalledOn?.value || p?.InstalledOn || p?.InstalledOn?.DateTime;
      const dt = parseSlashDate(v);
      if (dt && (!latest || dt.getTime() > latest.getTime())) latest = dt;
    }
    if (!latest) return patches.length > 0;
    return now - latest.getTime() <= maxAgeMs;
  },

  securityPatches: (d) => {
    if (!d) return false;
    const patches = Array.isArray(d.securityPatches) ? d.securityPatches : [];
    const pending = Array.isArray(d.pendingUpdates) ? d.pendingUpdates : [];
    if (patches.length === 0) return false;
    const now = Date.now();
    for (const p of pending) {
      const dt = parseSlashDate(p?.InstalledOn?.value || p?.InstalledOn);
      if (dt && dt.getTime() > now) return false;
      if (!dt && String(p?.Description || "").toLowerCase().includes("security update")) return false;
    }
    return true;
  },

  firewall: (d) => {
    if (!Array.isArray(d?.firewall) || d.firewall.length === 0) return false;
    const required = ["Domain", "Private", "Public"];
    for (const r of required) {
      const found = d.firewall.find((f) => String(f?.Name || "").toLowerCase() === r.toLowerCase());
      if (!found || Number(found?.Enabled) !== 1) return false;
    }
    return true;
  },

  antivirus: (d) => {
    const av = d?.antivirus;
    if (!av) return false;
    if (av.displayName && String(av.displayName).trim() !== "") return true;
    if (typeof av.productState !== "undefined" && Number(av.productState) > 0) return true;
    return false;
  },

  realTimeProtection: (d) => {
    if (d?.defenderRealtime) return true;
    const av = d?.antivirus;
    if (av && typeof av.productState !== "undefined") return Number(av.productState) > 0;
    return false;
  },

  diskEncrypted: (d) => {
    const b = d?.bitlocker;
    if (!b) return false;
    const vs = b.VolumeStatus ?? b.VolumeStatus;
    return Number(vs) === 1;
  },

  secureBootEnabled: (d) => {
    return !!d?.secureBoot;
  },

  trustedCAs: (d) => {
    const arr = Array.isArray(d?.trustedCAs) ? d.trustedCAs : [];
    if (arr.length === 0) return false;
    const now = Date.now();
    for (const cert of arr) {
      const subj = String(cert?.Subject || "").toLowerCase();
      if (
        subj.includes("local") ||
        subj.includes("squid") ||
        subj.includes("proxy") ||
        subj.includes("self-signed") ||
        subj.includes("local-ca") ||
        subj.includes("squid-local")
      ) {
        return false;
      }
      const na = cert?.NotAfter;
      const dt = parseSlashDate(na);
      if (dt && dt.getTime() < now) return false;
    }
    return true;
  },

  usbDevices: (d) => {
    const arr = Array.isArray(d?.usbDevices) ? d.usbDevices : [];
    if (arr.length === 0) return true;
    for (const u of arr) {
      const name = String(u?.FriendlyName || "").toLowerCase();
      const iid = String(u?.InstanceId || "").toLowerCase();
      if (name.includes("mass storage") || name.includes("external") || name.includes("external storage") || name.includes("removable")) return false;
      if (iid.includes("usb") && (name.includes("composite device") || name.includes("storage") || name.includes("disk"))) return false;
    }
    return true;
  },

  networkPrivilege: (d) => {
    const up = String(d?.userPrivilege || "").toLowerCase();
    if (!up) return false;
    return !(up.includes("administrator") || up.includes("admin"));
  },
};

const QUESTION_KEYS = Object.keys(CONTROL_WEIGHTS);
const DEFAULT_DEPARTMENTS = [
  { id: "Technical", label: "Technical" },
  { id: "Non-Technical", label: "Non-Technical" },
  { id: "Admin", label: "Admin" },
];

export default function RiskAnalysis() {
  const [departments, setDepartments] = useState(DEFAULT_DEPARTMENTS);
  const [selectedDept, setSelectedDept] = useState(DEFAULT_DEPARTMENTS[0].id);
  const [deptScoreMap, setDeptScoreMap] = useState({});
  const [loading, setLoading] = useState(true);

  const [percent, setPercent] = useState(0); 

  // Use signal colors from DESIGN.md
  const gaugeColors = ["#00A88F", "#F0A202", "#E2622B", "#D6323A"];

  const loadAll = async () => {
    setLoading(true);
    try {
      const [qResp, devResp, userResp] = await Promise.all([
        request("questionnaire", { method: "GET" }).catch(() => null),
        request("devices", { method: "GET" }).catch(() => null),
        request("user", { method: "GET" }).catch(() => null),
      ]);

      let questionnaire = null;
      if (!qResp) questionnaire = null;
      else if (qResp.data && typeof qResp.data === "object") questionnaire = qResp.data;
      else if (Array.isArray(qResp) && qResp.length > 0) questionnaire = qResp[0];
      else if (typeof qResp === "object") questionnaire = qResp;
      else questionnaire = null;

      const devices = normalizeArrayResp(devResp);
      const users = normalizeArrayResp(userResp);

      const userDeptById = new Map();
      const deptSet = new Set();
      for (const u of users) {
        if (!u) continue;
        const id = u._id || u.id || u.userId || (u._id && u._id.toString && u._id.toString());
        const dept = u.department || u.departmentName || u.dept || "Unassigned";
        if (id) userDeptById.set(String(id), String(dept));
        deptSet.add(String(dept));
      }

      const resolvedDepartments = deptSet.size > 0 ? Array.from(deptSet).map((d) => ({ id: d, label: d })) : DEFAULT_DEPARTMENTS;
      setDepartments(resolvedDepartments);
      if (!resolvedDepartments.find((d) => d.id === selectedDept)) {
        setSelectedDept(resolvedDepartments[0].id);
      }

      const applicableKeys = QUESTION_KEYS.filter((k) => String(questionnaire?.[k] ?? "").toUpperCase() === "YES");
      const totalApplicableWeight = applicableKeys.reduce((s, k) => s + (CONTROL_WEIGHTS[k] || 0), 0);

      const denom = totalApplicableWeight === 0 ? 100 : totalApplicableWeight;

      const deptAcc = new Map(); 

      for (const d of devices) {
        const uid = String(d?.userId || d?.user || d?.user_id || "");
        const dept = userDeptById.get(uid) || "Unassigned";

        let awarded = 0;
        for (const k of applicableKeys) {
          try {
            const ok = !!deviceChecks[k](d);
            if (ok) awarded += CONTROL_WEIGHTS[k] || 0;
          } catch (e) {
            
          }
        }

        const deviceScore = denom === 0 ? 1.0 : awarded / denom;
        const prev = deptAcc.get(dept) || { sum: 0, count: 0 };
        prev.sum += deviceScore;
        prev.count += 1;
        deptAcc.set(dept, prev);
      }

      const final = {};
      for (const dept of resolvedDepartments) {
        const v = deptAcc.get(dept.id);
        final[dept.id] = v && v.count > 0 ? v.sum / v.count : 0;
      }
      for (const [dept, agg] of deptAcc.entries()) {
        if (!(dept in final)) final[dept] = agg.count > 0 ? agg.sum / agg.count : 0;
      }

      setDeptScoreMap(final);

      const curSecure = final[selectedDept] ?? 0;
      const curRisk = Math.max(0, Math.min(1, 1 - curSecure));
      setPercent(curRisk);
    } catch (err) {
      console.error("RiskAnalysis loadAll error:", err);
      setDeptScoreMap({});
      setPercent(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const score = deptScoreMap[selectedDept] ?? 0;
    const risk = Math.max(0, Math.min(1, 1 - score));
    setPercent(risk);
  }, [selectedDept, deptScoreMap]);

  const scoreForSelected = deptScoreMap[selectedDept] ?? 0;
  const riskPercent = Math.round((1 - (scoreForSelected || 0)) * 100);

  const riskColor = scoreForSelected >= 0.85 ? "var(--secure)" : scoreForSelected >= 0.6 ? "var(--elevated)" : "var(--critical)";

  return (
    <div className="widget-card risk-widget" style={{ width: "100%", height: "100%" }}>
      <div 
        className="global-card" 
        style={{ 
          display: "flex", 
          flexDirection: "column", 
          height: "100%", 
          padding: "1.25rem" 
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
          <h5 className="global-section-title" style={{ margin: 0 }}>
            Risk Analysis
          </h5>

          {/* EXACT Match with Custom Dark/Light Arrow Wrapper */}
          <div style={{ position: "relative", width: "150px" }}>
            <select
              className="global-input"
              value={selectedDept}
              onChange={(e) => setSelectedDept(e.target.value)}
              style={{
                width: "100%",
                cursor: "pointer",
                fontSize: "14px",
                padding: "6px 28px 6px 12px", 
                backgroundColor: "transparent",
                color: "var(--vault-navy)",
                border: "1px solid var(--hairline)",
                borderRadius: "6px",
                boxShadow: "none",
                outline: "none",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                backgroundImage: "none" // 👈 Completely kills the native/Bootstrap arrow
              }}
            >
              {departments.map((d) => (
                <option 
                  key={d.id || d} 
                  value={d.id || d} 
                  style={{ backgroundColor: 'var(--canvas)', color: 'var(--vault-navy)' }}
                >
                  {d.label || (d === "all" ? "All Departments" : d)}
                </option>
              ))}
            </select>
            
            {/* Custom SVG Arrow that automatically adapts to dark mode via CSS variables */}
            <div
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                pointerEvents: "none", 
                color: "var(--vault-navy)", 
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 2L6 6.5L10.5 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          {loading ? (
            <div style={{ textAlign: "center", color: "var(--slate)" }}>Loading…</div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "center", width: "100%", minWidth: 0, marginBottom: "1rem" }}>
                <div style={{ width: "95%", maxWidth: "300px", minWidth: 0, display: "flex", justifyContent: "center", overflow: "hidden" }}>
                   <GaugeChart
                      id="risk-gauge-chart"
                      nrOfLevels={4}
                      colors={gaugeColors} 
                      arcWidth={0.28}
                      percent={percent} 
                      animate={false}
                      needleColor={"var(--navy)"}      
                      needleBaseColor={"var(--navy)"}  
                      hideText={true}
                      cornerRadius={3}
                      style={{ width: "100%", maxWidth: "100%" }} 
                    />
                </div>
              </div>

              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: riskColor, marginBottom: "4px" }}>
                  {riskPercent}% risk
                </div>
                <div className="global-caption text-primary" style={{ fontWeight: 600 }}>
                  {departments.find((d) => d.id === selectedDept)?.label || selectedDept} Department
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}